import React from "react";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { Add as AddIcon, Home as HomeIcon, ChevronRight as ChevronRightIcon } from "@vedmarket/ui-kit";
import { Star as StarIcon } from '@vedmarket/ui-kit';

import {useGetSupportUnreadCountQuery} from "../../../../../api/support";
import {COLORS} from "../../../../../config/constants";
import {useSupportUnreadCount} from "../../support-hooks";

import './support-page-header.scss'

type Props = {
  hideCreateButton?: boolean;
  isCreatingRequest: boolean;
  onBackToSupport?(): void;
  title?: React.ReactNode;
}

const SupportPageHeader = ({hideCreateButton = false, isCreatingRequest, onBackToSupport, title}: Props) => {

  const { t } = useTranslation();

  const unreadCount = useSupportUnreadCount();
  useGetSupportUnreadCountQuery(undefined, {refetchOnMountOrArgChange: true});

  const hasSupportBreadcrumb = isCreatingRequest || Boolean(title);
  const showUnreadCount = !isCreatingRequest && !title && unreadCount > 0;

  const handleBackToSupport = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onBackToSupport && onBackToSupport();
  };

  return (
    <header className='support-page-header'>
      <div className='support-page-header__heading'>
        <Breadcrumbs
          aria-label={t('support__text__breadcrumb')}
          className='support-page-header__breadcrumbs'
          separator={<ChevronRightIcon />}
          sx={{height: '22px', '.MuiBreadcrumbs-ol': {height: '22px'}}}
        >
          <Link
            href='/cabinet'
            underline='hover'
            className='support-page-header__home-link'
            sx={{
              alignItems: 'center',
              color: COLORS.MEDIUM_GRAY,
              cursor: 'pointer',
              display: 'flex',
              gap: '8px',
              height: '22px',
            }}
          >
            <HomeIcon className='support-page-header__home-icon' />
            <Typography
              variant='body2'
              sx={{color: COLORS.MEDIUM_GRAY, letterSpacing: 0, lineHeight: '22px'}}
            >
              {t('menu__text__main')}
            </Typography>
          </Link>
          {hasSupportBreadcrumb && (
            <div className='support-page-header__link'>
              <StarIcon className='support-page-header__support-icon' color={COLORS.YELLOW} />
              <Link
                href='/cabinet/support'
                underline='hover'
                className='support-page-header__support-link'
                onClick={handleBackToSupport}
                sx={{
                  color: COLORS.MEDIUM_GRAY,
                  cursor: 'pointer',
                  display: 'flex',
                  height: '22px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{color: COLORS.MEDIUM_GRAY, letterSpacing: 0, lineHeight: '22px'}}
                >
                  {t('support__text__title')}
                </Typography>
              </Link>
            </div>
          )}
        </Breadcrumbs>
        <Typography
          variant='h4'
          className='support-page-header__title'
          sx={{fontSize: '32px', fontWeight: 700, lineHeight: '40px', position: 'relative'}}
        >
          {title || t(isCreatingRequest ? 'support__text__newRequestTitle' : 'support__text__title')}
          {showUnreadCount && (
            <Typography
              component='span'
              variant='h4'
              sx={{
                alignItems: 'center',
                color: COLORS.LIGHT_TEXT_DISABLED,
                display: 'flex',
                fontSize: '18px',
                left: 'calc(100% + 8px)',
                position: 'absolute',
                top: '4px',
              }}
            >
              +{unreadCount}
            </Typography>
          )}
        </Typography>
      </div>
      {!isCreatingRequest && !hideCreateButton && (
        <Button
          variant='contained'
          component={RouterLink}
          to='/cabinet/support/new'
          className='support-page-header__create-button'
          startIcon={<AddIcon />}
        >
          {t('support__action__newRequest')}
        </Button>
      )}
    </header>
  );
};

export default SupportPageHeader;
